import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BuildBadge } from "@/components/BuildBadge";
import {
  buildKindOf,
  fmtDurationMs,
  type JobBuildFields,
} from "@/lib/buildLabels";

type RunnerJob = JobBuildFields & {
  id: string;
  entry: string;
  started_at: number | null;
  created_at: number;
};

/** The job id a runner entity's status says it is processing, or null. */
export function processingJobId(
  detail: Record<string, unknown> | null | undefined,
): string | null {
  if (!detail || detail["occupancy"] !== "processing") return null;
  const job = (detail["job"] ?? null) as Record<string, unknown> | null;
  return typeof job?.["jobId"] === "string" ? job["jobId"] : null;
}

/** What a busy runner is building: "#3 final (PGO) · linux-x64 · musl",
 *  with the job's entry and how long it has been running. Looked up by id
 *  (admin jobs list `ids=`); renders nothing for an idle runner and falls
 *  back to the plain job link when the job carries no build label. */
export function RunnerBuild({
  call,
  detail,
}: {
  call: <T>(path: string) => Promise<T>;
  detail: Record<string, unknown> | null | undefined;
}) {
  const jobId = processingJobId(detail);
  const [job, setJob] = useState<RunnerJob | null>(null);
  useEffect(() => {
    setJob(null);
    if (!jobId) return;
    let cancelled = false;
    call<{ ok: true; jobs: RunnerJob[] }>(
      `/admin/jobs?ids=${encodeURIComponent(jobId)}&limit=1`,
    )
      .then((r) => {
        if (!cancelled) setJob(r.jobs[0] ?? null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [call, jobId]);
  if (!jobId) return null;
  const labelled = job && buildKindOf(job);
  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-muted-foreground">
      <span>building</span>
      {job && labelled ? <BuildBadge job={job} /> : null}
      <Link
        to={`/logs/${encodeURIComponent(jobId)}`}
        className="font-mono underline decoration-dotted"
      >
        {job && !labelled ? `${job.entry} · ` : ""}job {jobId.slice(0, 8)}
      </Link>
      {job && (
        <span className="tabular-nums">
          {fmtDurationMs(job.started_at ?? job.created_at, null)}
        </span>
      )}
    </div>
  );
}
